/*
PURPOSE: Centralized gesture perspective and interaction logic

USE CASES:
- Determine user's relationship to a gesture (sender, receiver, witness)
- Calculate UI states based on perspective (bold names, button labels, etc.)
- Provide consistent gesture-related utilities across components
- Derive return gesture labels with theme-aware defaults

DOMAIN BOUNDARIES:
- Handles perspective logic and UI state calculation
- Does NOT handle rendering or API calls
- Provides pure functions for gesture-related determinations

ASSUMPTIONS:
- User IDs are unique and comparable via ===
- Perspective is determined by current user vs sender/target relationship
*/

import { GestureTheme, GestureType } from 'shared/constants/gestureThemes';

export type UserPerspective = 'sender' | 'receiver' | 'witness';

/**
 * Determine the current user's perspective on a gesture
 *
 * @param currentUserId - ID of the currently authenticated user
 * @param senderId - ID of the gesture sender
 * @param targetId - ID of the gesture target/receiver
 * @returns 'sender' if current user sent it, 'receiver' if they received it, 'witness' if third party
 */
export function getUserPerspective(
  currentUserId: string | undefined,
  senderId: string | undefined,
  targetId: string | undefined
): UserPerspective {
  if (!currentUserId) return 'witness';
  if (currentUserId === senderId) return 'sender';
  if (currentUserId === targetId) return 'receiver';
  return 'witness';
}

/**
 * Determine if a user should be displayed as "You" or "you"
 *
 * @param userId - ID of the user being displayed
 * @param currentUserId - ID of the currently authenticated user
 * @returns true if the user is the current user
 */
export function isCurrentUser(
  userId: string | undefined,
  currentUserId: string | undefined
): boolean {
  return !!userId && !!currentUserId && userId === currentUserId;
}

/**
 * Determine if a user's name should be bold (the "other party")
 * In gesture interactions, we bold the person who is NOT you
 *
 * @param role - Whether this user is the 'sender' or 'target' of the gesture
 * @param perspective - Current user's perspective on the gesture
 * @returns true if the name should be displayed in bold
 */
export function shouldBoldName(
  role: 'sender' | 'target',
  perspective: UserPerspective
): boolean {
  // Bold the sender when you're the receiver
  if (role === 'sender' && perspective === 'receiver') return true;
  // Bold the target when you're the sender
  if (role === 'target' && perspective === 'sender') return true;
  // Don't bold in witness mode
  return false;
}

/**
 * Get the appropriate button label for interacting with a gesture
 *
 * @param perspective - Current user's perspective on the gesture
 * @returns 'Say something' if user is sender, 'Respond' otherwise
 */
export function getInteractionButtonLabel(perspective: UserPerspective): string {
  return perspective === 'sender' ? 'Say something' : 'Respond';
}

/**
 * Get the return label and past tense for a gesture type within a theme
 * Uses explicit overrides if defined, otherwise derives sensible defaults
 *
 * Note: Handles mapping from content types ('boogie') to theme types ('dance_with')
 *
 * @param theme - The gesture theme object
 * @param gestureType - The type of gesture (smile, nod, dance_with, boogie)
 * @returns Object with returnLabel (button text) and returnPast (past tense for display)
 */
export function getReturnLabel(
  theme: GestureTheme,
  gestureType: string
): { returnLabel: string; returnPast: string } {
  // Map content type 'boogie' to theme type 'dance_with'
  const themeGestureType = gestureType === 'boogie' ? 'dance_with' : gestureType;
  const gestureLabel = theme.gestures[themeGestureType as GestureType];

  // Use explicit override if defined, otherwise derive default
  const returnLabel = gestureLabel.returnLabel ?? `${gestureLabel.label} back`;

  // For returnPast, use override or derive from base past tense
  // Default derivation: append "back" intelligently
  const returnPast = gestureLabel.returnPast ?? deriveReturnPast(gestureLabel.past);

  return { returnLabel, returnPast };
}

/**
 * Derive a sensible return past tense from the base past tense
 * E.g., "smiled at" -> "smiled back at", "nodded to" -> "nodded back to"
 *
 * @param basePast - The base past tense (e.g., "smiled at")
 * @returns Derived return past tense (e.g., "smiled back at")
 */
function deriveReturnPast(basePast: string): string {
  // Handle common patterns: "smiled at", "nodded to", "boogied with"
  const patterns = [
    { suffix: ' at', replacement: ' back at' },
    { suffix: ' to', replacement: ' back to' },
    { suffix: ' with', replacement: ' back with' },
  ];

  for (const { suffix, replacement } of patterns) {
    if (basePast.endsWith(suffix)) {
      return basePast.slice(0, -suffix.length) + replacement;
    }
  }

  // Fallback: just append " back"
  return `${basePast} back`;
}
